// Bulk editor for Issues.
//
// This handles selection of multiple issues and then *doing things* to them:
// things like labeling, user assignment, and so on.

import {fetchPoll} from '@github-ui/fetch-utils'
import {announce} from '@github-ui/aria-live'
import {on} from 'delegated-events'
import {softNavigate} from '@github-ui/soft-navigate'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {reportError} from '@github-ui/failbot'
import {currentState, updateCurrentState, updateUrl} from '@github-ui/history'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  getNavigationTabCountUpdateVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import type {NavigationTabCountUpdateResult} from '@github-ui/global-navigation'
import type {HistoryState} from '@github-ui/history'
import {showGlobalError} from '../behaviors/ajax-error'
import {reloadPageController} from './reload-page'

const loadables = new WeakMap()
const pendingForms = new WeakMap()
const pullRequestTriageCountSyncKey = 'pull-request-triage-count-sync'
const pullRequestTriageErrorKey = 'pull-request-triage-error'
const countSelectors = {
  open: "a[data-ga-click='Pull Requests, Table state, Open']",
  closed: "a[data-ga-click='Pull Requests, Table state, Closed']",
}

function formatPullRequestCount(count: number): string {
  return count.toLocaleString('en-US')
}

function pullRequestCountUpdateMessage(counts: {open: number; closed: number}): string {
  // eslint-disable-next-line i18n-text/no-en
  return `Pull request counts updated: ${formatPullRequestCount(counts.open)} open and ${formatPullRequestCount(counts.closed)} closed`
}

function pullRequestCountRollbackMessage(counts?: {open: number; closed: number}, restored = false): string {
  if (!counts) {
    // eslint-disable-next-line i18n-text/no-en
    return 'Bulk action processing failed. Please try again.'
  }

  if (restored) {
    // eslint-disable-next-line i18n-text/no-en
    return `Bulk action processing failed. Pull request counts were restored to ${formatPullRequestCount(counts.open)} open and ${formatPullRequestCount(counts.closed)} closed. Please try again.`
  }

  // eslint-disable-next-line i18n-text/no-en
  return `Bulk action processing failed. Pull request counts were left at ${formatPullRequestCount(counts.open)} open and ${formatPullRequestCount(counts.closed)} closed. Please try again.`
}

function showPullRequestTriageError(message = pullRequestCountRollbackMessage()) {
  const ajaxErrorMessage = document.getElementById('ajax-error-message')
  if (ajaxErrorMessage) {
    const messageNode = Array.from(ajaxErrorMessage.childNodes).find(
      node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
    )
    if (messageNode) {
      messageNode.textContent = message
    } else {
      ajaxErrorMessage.append(message)
    }
    showGlobalError()
  }
  if (!ajaxErrorMessage?.closest('[role="alert"], [role="status"], [aria-live]:not([aria-live="off"])')) {
    announce(message, {assertive: true})
  }
}

function currentPagePath(): string {
  return `${window.location.pathname}${window.location.search}`
}

function refreshUrlAfterPolling(syncUrl: string): {url: string; changedHash: boolean} {
  const initialUrl = new URL(syncUrl, window.location.origin)
  const currentUrl = new URL(window.location.href, window.location.origin)
  if (currentUrl.hash !== initialUrl.hash) {
    return {url: currentUrl.toString(), changedHash: true}
  }

  initialUrl.hash = ''
  return {url: initialUrl.toString(), changedHash: false}
}

type TriageHistoryState = HistoryState & {[pullRequestTriageCountSyncKey]?: string}

function countSyncMarkerFromHistory(): string | null {
  const marker = (currentState() as TriageHistoryState)[pullRequestTriageCountSyncKey]
  return typeof marker === 'string' ? marker : null
}

function storedCountSyncMarker(): string | null {
  try {
    return sessionStorage.getItem(pullRequestTriageCountSyncKey) ?? countSyncMarkerFromHistory()
  } catch (error) {
    reportError(error)
    return countSyncMarkerFromHistory()
  }
}

function setStoredCountSyncMarker(value: string): 'session' | 'history' | 'failed' {
  try {
    sessionStorage.setItem(pullRequestTriageCountSyncKey, value)
    return 'session'
  } catch (error) {
    reportError(error)
  }

  try {
    updateCurrentState({[pullRequestTriageCountSyncKey]: value} as TriageHistoryState)
    return 'history'
  } catch (error) {
    reportError(error)
    return 'failed'
  }
}

export function refreshPullRequestTriagePage(
  syncUrl: string,
  countSyncMarkerStorage?: 'session' | 'history' | 'failed',
) {
  const {url: refreshUrl, changedHash} = refreshUrlAfterPolling(syncUrl)
  if (countSyncMarkerStorage === 'history' || changedHash) {
    updateUrl(refreshUrl)
    reloadPageController.reloadPage()
    return
  }

  softNavigate(refreshUrl, {action: 'replace'})
}

function clearStoredCountSyncMarker(expectedValue: string) {
  try {
    if (sessionStorage.getItem(pullRequestTriageCountSyncKey) === expectedValue) {
      sessionStorage.removeItem(pullRequestTriageCountSyncKey)
    }
  } catch (error) {
    reportError(error)
  }

  const state = currentState() as TriageHistoryState
  if (state[pullRequestTriageCountSyncKey] !== expectedValue) return

  updateCurrentState({[pullRequestTriageCountSyncKey]: undefined} as TriageHistoryState)
}

function listingElementCount(element: HTMLElement | null): number | undefined {
  const count = element?.textContent?.match(/([\d,]+)\s+(?:Open|Closed)/)
  const numericText = count?.[1]
  if (!numericText) return

  const numericCount = Number(numericText.replaceAll(',', ''))
  return Number.isSafeInteger(numericCount) ? numericCount : undefined
}

function listingCount(selector: string): number | undefined {
  return listingElementCount(document.querySelector<HTMLElement>(selector))
}

type PullRequestListingTargets = {open: HTMLElement[]; closed: HTMLElement[]}

function pullRequestListingTargets(): PullRequestListingTargets {
  return {
    open: Array.from(document.querySelectorAll<HTMLElement>(countSelectors.open)),
    closed: Array.from(document.querySelectorAll<HTMLElement>(countSelectors.closed)),
  }
}

function listingTargetsStillOwnCounts(targets: PullRequestListingTargets, counts: {open: number; closed: number}) {
  const currentTargets = pullRequestListingTargets()
  return (['open', 'closed'] as const).every(
    state =>
      targets[state].length > 0 &&
      targets[state].length === currentTargets[state].length &&
      targets[state].every(
        (target, index) => target === currentTargets[state][index] && listingElementCount(target) === counts[state],
      ),
  )
}

interface PullRequestCountTransition {
  before: {open: number; closed: number}
  after: {open: number; closed: number}
}

interface OptimisticNavigation {
  result: NavigationTabCountUpdateResult
  abortController: AbortController
  settlement?: 'applied' | 'rejected'
  updateVersion?: number
  authoritativeVersion?: number
  targetVersion?: number
}

export function defaultPullRequestCountTransitionAfterClose(
  url = window.location.href,
): PullRequestCountTransition | undefined {
  const parsedUrl = new URL(url, window.location.origin)
  if (!parsedUrl.pathname.match(/\/pulls\/?$/) || parsedUrl.search) return

  const open = listingCount(countSelectors.open)
  const closed = listingCount(countSelectors.closed)
  const selectedCount = document.querySelectorAll('.js-issue-row .js-issues-list-check:checked').length
  if (open === undefined || closed === undefined || selectedCount === 0) return

  return {
    before: {open, closed},
    after: {
      open: Math.max(0, open - selectedCount),
      closed: closed + selectedCount,
    },
  }
}

function synchronizeListingCount(selector: string, count: number): {found: boolean; changed: boolean} {
  const formattedCount = count.toLocaleString('en-US')
  let found = false
  let changed = false

  for (const listingLink of document.querySelectorAll<HTMLElement>(selector)) {
    const countText = Array.from(listingLink.childNodes).find(
      node => node.nodeType === Node.TEXT_NODE && /[\d,]+/.test(node.textContent ?? ''),
    )
    if (!countText?.textContent) continue

    found = true
    const nextText = countText.textContent.replace(/[\d,]+/, formattedCount)
    if (nextText !== countText.textContent) {
      countText.textContent = nextText
      changed = true
    }
  }

  return {found, changed}
}

function disableListingCountLiveRegions() {
  for (const listingLink of document.querySelectorAll<HTMLElement>(
    `${countSelectors.open}, ${countSelectors.closed}`,
  )) {
    listingLink.closest<HTMLElement>('[aria-live]')?.setAttribute('aria-live', 'off')
  }
}

function synchronizePullRequestListingCountsWithAnnouncement(
  counts: {open: number; closed: number},
  message?: string,
): boolean {
  disableListingCountLiveRegions()
  const openUpdated = synchronizeListingCount(countSelectors.open, counts.open)
  const closedUpdated = synchronizeListingCount(countSelectors.closed, counts.closed)
  if (message && (openUpdated.changed || closedUpdated.changed)) {
    announce(message)
  }
  return openUpdated.found && closedUpdated.found
}

export function synchronizePullRequestListingCounts(counts: {open: number; closed: number}): boolean {
  return synchronizePullRequestListingCountsWithAnnouncement(counts, pullRequestCountUpdateMessage(counts))
}

function optimisticNavigationOwnership(navigation: OptimisticNavigation): 'pending' | 'current' | undefined {
  if (navigation.targetVersion !== getNavigationTabCountTargetVersion('pull-requests')) return

  if (navigation.result === 'queued' && navigation.settlement === undefined) return 'pending'
  if (
    navigation.settlement === 'applied' &&
    navigation.updateVersion === getNavigationTabCountUpdateVersion('pull-requests')
  ) {
    return 'current'
  }
}

export function applyOptimisticPullRequestCountTransition(
  transition: PullRequestCountTransition,
  repositoryNwo?: string,
): {applied: boolean; navigation: OptimisticNavigation} {
  const navigation: OptimisticNavigation = {result: 'rejected', abortController: new AbortController()}
  navigation.result = updateNavigationTabCount(
    'pull-requests',
    {
      type: 'countIfCurrent',
      count: transition.after.open,
      expectedCounts: [transition.before.open],
    },
    {
      repositoryNwo,
      signal: navigation.abortController.signal,
      onSettled: result => {
        navigation.settlement = result
        if (result === 'applied') {
          navigation.updateVersion = getNavigationTabCountUpdateVersion('pull-requests')
          navigation.authoritativeVersion = getNavigationTabCountAuthoritativeVersion('pull-requests')
          navigation.targetVersion = getNavigationTabCountTargetVersion('pull-requests')
        }
      },
    },
  )

  const currentNavigationCount = getNavigationTabCountSnapshot('pull-requests')
  const listingUpdated =
    (navigation.result !== 'rejected' ||
      (currentNavigationCount?.exact === true && currentNavigationCount.count === transition.after.open)) &&
    synchronizePullRequestListingCounts(transition.after)
  const applied = listingUpdated || navigation.result !== 'rejected'
  if (applied) {
    navigation.updateVersion ??= getNavigationTabCountUpdateVersion('pull-requests')
    navigation.targetVersion ??= getNavigationTabCountTargetVersion('pull-requests')
  }

  return {applied, navigation}
}

function rollbackOptimisticPullRequestCountTransition(
  transition: PullRequestCountTransition,
  navigation: OptimisticNavigation,
  repositoryNwo?: string,
): {open: number; closed: number} | undefined {
  const settlement = navigation.settlement
  navigation.abortController.abort()
  if (settlement === undefined) return transition.before
  if (settlement !== 'applied') return navigation.result === 'queued' ? transition.before : undefined

  const result = updateNavigationTabCount(
    'pull-requests',
    {
      type: 'delta',
      delta: transition.before.open - transition.after.open,
    },
    {
      repositoryNwo,
      expectedUpdateVersion: navigation.authoritativeVersion,
      expectedTargetVersion: navigation.targetVersion,
    },
  )
  const currentNavigationCount = getNavigationTabCountSnapshot('pull-requests')
  if (result !== 'applied' || currentNavigationCount?.exact !== true) return

  return {
    open: currentNavigationCount.count,
    closed: transition.before.closed + transition.before.open - currentNavigationCount.count,
  }
}

export function synchronizePullRequestListingCountsAfterTriage() {
  try {
    const storedValue = storedCountSyncMarker()
    if (!storedValue) return

    const pendingSync = JSON.parse(storedValue)
    const clearPendingSync = () => {
      clearStoredCountSyncMarker(storedValue)
    }
    const isCurrent =
      pendingSync.pagePath === currentPagePath() &&
      Date.now() - pendingSync.createdAt < 60_000 &&
      Number.isSafeInteger(pendingSync.open) &&
      Number.isSafeInteger(pendingSync.closed) &&
      Number.isSafeInteger(pendingSync.beforeOpen) &&
      Number.isSafeInteger(pendingSync.beforeClosed)
    if (!isCurrent) {
      clearPendingSync()
      return
    }

    const currentCounts = {
      open: listingCount(countSelectors.open),
      closed: listingCount(countSelectors.closed),
    }
    const matchesBefore =
      currentCounts.open === pendingSync.beforeOpen && currentCounts.closed === pendingSync.beforeClosed
    const matchesAfter = currentCounts.open === pendingSync.open && currentCounts.closed === pendingSync.closed
    if (!matchesBefore && !matchesAfter) {
      clearPendingSync()
      return
    }

    updateNavigationTabCount(
      'pull-requests',
      {
        type: 'countIfCurrent',
        count: pendingSync.open,
        expectedCounts: [pendingSync.open],
      },
      {
        repositoryNwo: pendingSync.repositoryNwo,
        onSettled: result => {
          try {
            if (storedCountSyncMarker() !== storedValue) return
            clearStoredCountSyncMarker(storedValue)
            if (result !== 'applied' || pendingSync.pagePath !== currentPagePath()) return

            const latestCounts = {
              open: listingCount(countSelectors.open),
              closed: listingCount(countSelectors.closed),
            }
            if (latestCounts.open === pendingSync.beforeOpen && latestCounts.closed === pendingSync.beforeClosed) {
              synchronizePullRequestListingCounts({open: pendingSync.open, closed: pendingSync.closed})
            }
          } catch (error) {
            reportError(error)
          }
        },
      },
    )
  } catch (error) {
    reportError(error)
  }
}

synchronizePullRequestListingCountsAfterTriage()

export function showPullRequestTriageErrorAfterRefresh() {
  try {
    const storedValue = sessionStorage.getItem(pullRequestTriageErrorKey)
    if (!storedValue) return
    sessionStorage.removeItem(pullRequestTriageErrorKey)

    const pendingError = JSON.parse(storedValue)
    if (pendingError.pagePath !== currentPagePath() || Date.now() - pendingError.createdAt >= 60_000) return

    const currentCounts = {
      open: listingCount(countSelectors.open),
      closed: listingCount(countSelectors.closed),
    }
    const restoredCounts =
      Number.isSafeInteger(pendingError.beforeOpen) &&
      Number.isSafeInteger(pendingError.beforeClosed) &&
      currentCounts.open === pendingError.beforeOpen &&
      currentCounts.closed === pendingError.beforeClosed
        ? {open: pendingError.beforeOpen, closed: pendingError.beforeClosed}
        : undefined
    const currentCountSummary =
      Number.isSafeInteger(currentCounts.open) && Number.isSafeInteger(currentCounts.closed)
        ? {open: currentCounts.open, closed: currentCounts.closed}
        : undefined

    showPullRequestTriageError(pullRequestCountRollbackMessage(restoredCounts ?? currentCountSummary, !!restoredCounts))
  } catch (error) {
    reportError(error)
  }
}

showPullRequestTriageErrorAfterRefresh()

on('change', '.js-issues-list-check', function () {
  // Enter triage mode once one item is checked.
  const checked = !!document.querySelector('.js-issues-list-check:checked')
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  document.querySelector<HTMLElement>('#js-issues-toolbar')!.classList.toggle('triage-mode', checked)

  // Mark menus as reloadable.
  for (const menu of document.querySelectorAll('.js-issue-triage-menu')) {
    loadables.set(menu, true)
  }
})

on('toggle', '.js-issue-triage-menu', toggleMenu, {capture: true})
on('details-menu-selected', '.js-issue-triage-menu details-menu', selectItem, {capture: true})

function toggleMenu(event: Event) {
  const details = event.currentTarget as Element
  if (details.hasAttribute('open')) {
    loadMenu(details)
  } else {
    submitMenu(details)
  }
}

// Fetch menu items based on currently selected issues.
function loadMenu(details: Element) {
  if (!loadables.has(details)) return

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const template = document.querySelector<HTMLTemplateElement>('.js-triage-loader-template')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = details.querySelector<HTMLElement>('.js-triage-deferred-content')!

  // clone the template, find the include-fragment, and set the src
  const clonedTemplate = template.content.cloneNode(true) as DocumentFragment
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const url = menuUrl(details.getAttribute('data-url')!, checkedIssues())
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  clonedTemplate.querySelector<HTMLElement>('include-fragment')!.setAttribute('src', url)

  // append the cloned template to the container after we've set the include-fragment src
  container.textContent = ''
  container.append(clonedTemplate)

  loadables.delete(details)
}

// Show progress spinner until page reloads.
function toggleLoading(menu: Element, show: boolean) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const toolbar = menu.closest<HTMLElement>('.js-issues-toolbar-triage')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  toolbar.querySelector<HTMLElement>('.js-issue-triage-spinner')!.hidden = !show
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  toolbar.querySelector<HTMLElement>('.js-issue-triage-error')!.hidden = true
}

function toggleError(menu: Element, show: boolean) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const toolbar = menu.closest<HTMLElement>('.js-issues-toolbar-triage')!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  toolbar.querySelector<HTMLElement>('.js-issue-triage-spinner')!.hidden = true
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  toolbar.querySelector<HTMLElement>('.js-issue-triage-error')!.hidden = !show
}

// Save menu selections and wait for job to complete before refreshing.
async function submitMenu(details: Element) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const form = details.querySelector<HTMLFormElement>('form')!
  if (!pendingForms.has(form)) return
  const state = form.querySelector<HTMLInputElement>(".js-issues-triage-fields input[name='state']")?.value
  const syncUrl = window.location.href
  const syncPagePath = currentPagePath()
  const repositoryNwo = getCurrentRepositoryNwo()
  const countTransition = state === 'closed' ? defaultPullRequestCountTransitionAfterClose(syncUrl) : undefined
  const countUpdateVersion = countTransition ? getNavigationTabCountUpdateVersion('pull-requests') : undefined
  const countTargetVersion = countTransition ? getNavigationTabCountTargetVersion('pull-requests') : undefined
  let optimisticCountsApplied = false
  let optimisticNavigation: OptimisticNavigation | undefined
  let optimisticListingTargets: PullRequestListingTargets | undefined

  toggleLoading(details, true)

  try {
    const response = await fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        ...getBaseFetchHeaders(),
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      const responseError = new Error()
      const statusText = response.statusText ? ` ${response.statusText}` : ''
      responseError.message = `HTTP ${response.status}${statusText}`
      throw responseError
    }
    const data = await response.json()

    pendingForms.delete(form)
    if (
      countTransition &&
      countUpdateVersion === getNavigationTabCountUpdateVersion('pull-requests') &&
      countTargetVersion === getNavigationTabCountTargetVersion('pull-requests') &&
      currentPagePath() === syncPagePath
    ) {
      optimisticListingTargets = pullRequestListingTargets()
      const optimisticResult = applyOptimisticPullRequestCountTransition(countTransition, repositoryNwo)
      optimisticCountsApplied = optimisticResult.applied
      optimisticNavigation = optimisticResult.navigation
    }
    await fetchPoll(data.job.url, {headers: {accept: 'application/json'}})
    if (currentPagePath() !== syncPagePath) return

    const navigationOwnership = optimisticNavigation && optimisticNavigationOwnership(optimisticNavigation)
    const currentNavigationCount = getNavigationTabCountSnapshot('pull-requests')
    const navigationConfirmedByAuthoritativeUpdate =
      optimisticNavigation?.settlement === 'applied' &&
      optimisticNavigation.authoritativeVersion !== undefined &&
      optimisticNavigation.authoritativeVersion < getNavigationTabCountAuthoritativeVersion('pull-requests') &&
      optimisticNavigation.targetVersion === getNavigationTabCountTargetVersion('pull-requests') &&
      currentNavigationCount?.exact === true &&
      currentNavigationCount.count === countTransition?.after.open
    const listingOnlyUpdate =
      optimisticNavigation?.result === 'rejected' ||
      (optimisticNavigation?.result === 'queued' &&
        optimisticNavigation.settlement === 'rejected' &&
        currentNavigationCount?.exact === true &&
        currentNavigationCount.count === countTransition?.after.open)
    if (
      countTransition &&
      optimisticCountsApplied &&
      (navigationOwnership === 'pending' ||
        navigationOwnership === 'current' ||
        navigationConfirmedByAuthoritativeUpdate ||
        listingOnlyUpdate)
    ) {
      const countSyncMarkerStorage = setStoredCountSyncMarker(
        JSON.stringify({
          ...countTransition.after,
          beforeOpen: countTransition.before.open,
          beforeClosed: countTransition.before.closed,
          repositoryNwo,
          pagePath: syncPagePath,
          createdAt: Date.now(),
        }),
      )
      refreshPullRequestTriagePage(syncUrl, countSyncMarkerStorage)
      return
    }
    refreshPullRequestTriagePage(syncUrl)
  } catch {
    if (optimisticCountsApplied) {
      const listingRollbackCounts =
        countTransition && optimisticNavigation
          ? rollbackOptimisticPullRequestCountTransition(countTransition, optimisticNavigation, repositoryNwo)
          : undefined
      if (currentPagePath() === syncPagePath) {
        const currentListingCounts = {
          open: listingCount(countSelectors.open),
          closed: listingCount(countSelectors.closed),
        }
        const countsRestored =
          countTransition &&
          listingRollbackCounts &&
          optimisticListingTargets &&
          listingTargetsStillOwnCounts(optimisticListingTargets, countTransition.after)
        const currentCountSummary =
          currentListingCounts.open !== undefined && currentListingCounts.closed !== undefined
            ? {open: currentListingCounts.open, closed: currentListingCounts.closed}
            : undefined
        const errorMessage = pullRequestCountRollbackMessage(
          countsRestored ? listingRollbackCounts : currentCountSummary,
          !!countsRestored,
        )
        let errorStored = false
        try {
          sessionStorage.setItem(
            pullRequestTriageErrorKey,
            JSON.stringify({
              pagePath: syncPagePath,
              createdAt: Date.now(),
              beforeOpen: countTransition?.before.open,
              beforeClosed: countTransition?.before.closed,
            }),
          )
          errorStored = true
        } catch (error) {
          reportError(error)
        }
        if (!errorStored) {
          if (countsRestored) {
            synchronizePullRequestListingCountsWithAnnouncement(listingRollbackCounts)
          }
          toggleLoading(details, false)
          showPullRequestTriageError(errorMessage)
          return
        }
        refreshPullRequestTriagePage(syncUrl)
        return
      }

      showPullRequestTriageError()
      return
    }
    toggleError(details, true)
  }
}

// Copy selected menu item into hidden field and schedule submit on menu close.
function selectItem(event: CustomEvent) {
  const item = event.detail.relatedTarget as Element
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const form = item.closest<HTMLFormElement>('form')!
  const input = createInput(item)
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const fields = form.querySelector<HTMLElement>('.js-issues-triage-fields')!
  const existing = fields.querySelector(`[name='${input.name}']`)
  if (existing) {
    existing.replaceWith(input)
  } else {
    fields.append(input)
  }
  pendingForms.set(form, true)
}

// Create a hidden form input from a selected menu item.
function createInput(item: Element): HTMLInputElement {
  const selected = item.getAttribute('aria-checked') === 'true'
  const name = item.getAttribute('name') || item.getAttribute('data-name')
  const value = item.getAttribute('value') || item.getAttribute('data-value')

  const input = document.createElement('input')
  input.type = 'hidden'
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  input.name = name!

  switch (item.getAttribute('role')) {
    case 'menuitem':
    case 'menuitemradio':
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      input.value = value!
      break
    case 'menuitemcheckbox':
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      input.value = selected ? value! : '0'
      break
  }

  return input
}

// Returns the list of selected issues to bulk update.
function checkedIssues(): Array<[string, string]> {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.js-issues-list-check:checked')).map(el => [
    el.name,
    el.value,
  ])
}

// Build the menu's remote URL from currently selected issues.
function menuUrl(baseUrl: string, pairs: Array<[string, string]>): string {
  const url = new URL(baseUrl, window.location.origin)
  const params = new URLSearchParams(url.search)
  for (const [name, value] of pairs) {
    params.append(name, value)
  }
  url.search = params.toString()
  return url.toString()
}
